import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import shortid from 'shortid';

import Dialog from '@visa/vds-react/dialog-styled';
import DialogCardBody from '@visa/vds-react/dialog-card-body-styled';
import DialogCardFooter from '@visa/vds-react/dialog-card-footer-styled';
import DialogCardHead from '@visa/vds-react/dialog-card-head-styled';
import DialogCardHeadIcon from '@visa/vds-react/dialog-card-head-icon-styled';
import Icon from '@visa/vds-react/icon-styled';

import CybsFormFooter from '@cybs-ui/components/lib/form-footer';
import styles from './component.styles';

const CybsDialog = React.forwardRef(
  (
    {
      body,
      customCSS,
      customFooter,
      dataTest,
      description,
      escapeExits,
      footerButtons,
      isOpen,
      i18n: i18nProp,
      onCloseDialog,
      title,
      modalStyle,
      type,
      ...props
    },
    ref
  ) => {
    // Generate new random UI
    const randomId = shortid.generate();

    // Use localization defaults for undefined strings
    const i18n = {
      ...CybsDialog.defaultProps.i18n,
      ...i18nProp
    };

    // Logic to pause focus during calls from cybs.ajax
    // Remove when react-aria-modal/focus-trap-react updates to use trapStack
    // Initial state determined by if pageloader is present
    const [isFocusPaused, setIsFocusPaused] = useState(Boolean(document.getElementById('modal-loader')));

    function pauseFocus() {
      setIsFocusPaused(true);
    }
    function resumeFocus() {
      setIsFocusPaused(false);
    }

    // eslint-disable-next-line no-unused-expressions
    useEffect(() => {
      // Addd event listers
      document.addEventListener('ajaxStart', pauseFocus);
      document.addEventListener('ajaxStop', resumeFocus);
      document.addEventListener('ajaxError', resumeFocus);
      return () => {
        // Cleanup
        document.removeEventListener('ajaxStart', pauseFocus);
        document.removeEventListener('ajaxStop', resumeFocus);
        document.removeEventListener('ajaxError', resumeFocus);
      };
    }, []);

    return (
      <Dialog
        aria-modal="true"
        aria-describedby={`${randomId}-body`}
        customCSS={theme => [styles(theme).dialogClass, customCSS(theme)]}
        data-test={`${dataTest}-cybsDialog`}
        escapeExits={escapeExits}
        onExit={onCloseDialog}
        open={isOpen}
        paused={isFocusPaused}
        ref={ref}
        titleId={`${randomId}-title`} // Required. Outputs aria-labelledby.
        underlayStyle={modalStyle}
        type={type}
        {...props}
      >
        <DialogCardHead customCSS={theme => styles(theme).header}>
          {type ? (
            <DialogCardHeadIcon>
              <Icon name={type} resolution="low" />
            </DialogCardHeadIcon>
          ) : null}
          <h2 id={`${randomId}-title`}>{type ? `${type.charAt(0).toUpperCase() + type.slice(1)}: ${i18n.title || title}` : i18n.title || title}</h2>
        </DialogCardHead>
        <DialogCardBody customCSS={theme => styles(theme).body}>
          {description !== undefined || i18n.description ? (
            <>
              <div id={`${randomId}-body`}>
                {i18n.description || description}
              </div>
              <div>{i18n.body || body}</div>
            </>
          ) : (
            <div id={`${randomId}-body`}>{i18n.body || body}</div>
          )}
        </DialogCardBody>
        {customFooter ? (
          <DialogCardFooter>{customFooter}</DialogCardFooter>
        ) : (
          <DialogCardFooter customCSS={theme => styles(theme).footer}>
            <CybsFormFooter dataTest={dataTest} rightButtons={footerButtons} />
          </DialogCardFooter>
        )}
      </Dialog>
    );
  }
);

CybsDialog.propTypes = {
  /**
   * Dialog content. If this is a string, use i18n.
   */
  body: PropTypes.node,

  /**
   * Styling function to allow CSS overrides via Emotion
   *
   * @ignore
   */
  customCSS: PropTypes.func,

  /**
   * Custom footer. By default it will use CybsFormFooter.
   */
  customFooter: PropTypes.element,

  /**
   * Data test.
   */
  dataTest: PropTypes.string.isRequired,

  /**
   * Dialog description. Used for interactive modals that have descriptive text above. Use i18n.description if just a string.
   */
  description: PropTypes.node,

  /**
   * By default, the Escape key exits the modal.
   */
  escapeExits: PropTypes.bool,
  /**
   * Buttons to pass to CybsFormFooter.
   */
  footerButtons: PropTypes.arrayOf(
    PropTypes.shape({
      colorScheme: PropTypes.string,
      label: PropTypes.string,
      dataTest: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      onKeyDown: PropTypes.func
    })
  ),

  /**
   * Strings for component
   */
  i18n: PropTypes.shape({
    body: PropTypes.string,
    description: PropTypes.string,
    dialog: PropTypes.string,
    title: PropTypes.string
  }),

  /**
   * If true, the dialog is open
   */
  isOpen: PropTypes.bool,

  /**
   * Callback fired on escape.
   */
  onCloseDialog: PropTypes.func,

  /**
   * Title of the dialog. Deprecated. Included for backward compatability. Use i18n prop for strings.
   */
  title: PropTypes.string,

  /**
   * Custom object styles for the underlayStyle prop from react-aria-modal. Refer to VDS dialog component source code.
   *
   * @ignore
   */
  modalStyle: PropTypes.shape({}),

  /**
   * Type of alert to show with the dialog (error, success, or warning)
   */
  type: PropTypes.string
};

CybsDialog.defaultProps = {
  body: '',
  customCSS: () => '',
  customFooter: null,
  description: undefined,
  escapeExits: true,
  footerButtons: [],
  i18n: { body: '', description: '', dialog: 'Dialog', title: '' },
  isOpen: false,
  onCloseDialog: () => {},
  title: '',
  modalStyle: { zIndex: 99999 },
  type: ''
};

CybsDialog.displayName = 'CybsDialog';

export default CybsDialog;
